/**
 * Rotulos da parametrizacao de modalidades: os dominios que a tela mostra por nome.
 *
 * Ficam no modulo, e nao em `shared/ui/statusLabels.ts`, porque sao opcoes de
 * formulario e nomes de campo, e nao situacoes com cor.
 */

export const situacoes = [
  { value: 'ativo', label: 'Ativo' },
  { value: 'inativo', label: 'Inativo' },
];

const rascunho = { value: 'rascunho', label: 'Rascunho (outra pessoa ativa)' };

/**
 * Opcoes de situacao do formulario de ramo, grupo e modalidade.
 *
 * O rascunho (P-CARGA) e escolha so na criacao. O item em rascunho mostra so ele: a
 * saida e a ativacao, feita por outra pessoa com justificativa, e o servidor recusa a
 * edicao que tente tira-lo de rascunho. O item que ja saiu nao volta.
 */
export function situacoesPara(atual?: string): { value: string; label: string }[] {
  if (atual === 'rascunho') return [rascunho];
  if (atual === undefined) return [...situacoes, rascunho];
  return situacoes;
}

/** Grupo de credito da modalidade (00018). */
export const gruposDeCreditoDaModalidade = [
  { value: '', label: 'Nao classificado' },
  { value: 'tradicional', label: 'Tradicional' },
  { value: 'estruturadas', label: 'Estruturadas' },
  { value: 'judicial', label: 'Judicial' },
  { value: 'recursal', label: 'Recursal' },
  { value: 'financeira', label: 'Financeira' },
];

/** Grupo de credito do grupo do OnPoint: o mesmo dominio do limite do tomador. */
export const gruposDeCreditoDoGrupo = [
  ...gruposDeCreditoDaModalidade,
  { value: 'fianca', label: 'Fianca' },
];

/** Modalidade de referencia do motor de credito (00018), com o grupo dela. */
export const referenciasDeCredito = [
  { value: '', label: 'Sem vinculo', grupo: '' },
  { value: 'licitante', label: 'Licitante', grupo: 'tradicional' },
  {
    value: 'execucao_obras',
    label: 'Execucao de Contratos - Obras',
    grupo: 'tradicional',
  },
  {
    value: 'execucao_fornecimento',
    label: 'Execucao de Contratos - Fornecimento',
    grupo: 'tradicional',
  },
  {
    value: 'execucao_prestacao_servico',
    label: 'Execucao de Contratos - Prestacao de Servicos',
    grupo: 'tradicional',
  },
  {
    value: 'manutencao_corretiva',
    label: 'Manutencao Corretiva',
    grupo: 'tradicional',
  },
  {
    value: 'adiantamento_pagamento',
    label: 'Adiantamento de Pagamento',
    grupo: 'tradicional',
  },
  { value: 'retencao_pagamento', label: 'Retencao de Pagamento', grupo: 'tradicional' },
  { value: 'concessao', label: 'Concessao', grupo: 'tradicional' },
  { value: 'judicial_execucao_fiscal', label: 'Execucao Fiscal', grupo: 'judicial' },
  { value: 'judicial_civel', label: 'Judicial Civel', grupo: 'judicial' },
  { value: 'judicial_trabalhista', label: 'Judicial Trabalhista', grupo: 'judicial' },
  {
    value: 'judicial_administrativo_tributario',
    label: 'Administrativo de Creditos Tributarios',
    grupo: 'judicial',
  },
];

/** Tipo do ramo (EType do OnPoint, significado a confirmar). */
export const tiposDeRamo = [
  { value: '', label: 'Nao informado' },
  { value: 'setor_publico', label: 'Setor publico' },
  { value: 'setor_privado', label: 'Setor privado' },
];

export function rotuloDe(
  opcoes: { value: string; label: string }[],
  valor: string | null | undefined,
): string {
  return opcoes.find((opcao) => opcao.value === (valor ?? ''))?.label ?? valor ?? '';
}

/** Nome das acoes da trilha no historico da modalidade e do grupo. */
const acoes: Record<string, string> = {
  'produtos.modalidade.create': 'Modalidade cadastrada',
  'produtos.modalidade.update': 'Cadastro alterado em produtos',
  'parametrizacao.modalidade.update': 'Cadastro da modalidade alterado',
  'parametrizacao.modalidade_configuracao.create': 'Rascunho de configuracao aberto',
  'parametrizacao.modalidade_configuracao.update': 'Rascunho de configuracao editado',
  'parametrizacao.modalidade_configuracao.approve': 'Versao de configuracao ativada',
  'parametrizacao.modalidade_configuracao.revoke': 'Versao de configuracao revogada',
  'parametrizacao.grupo.create': 'Grupo cadastrado',
  'parametrizacao.grupo.update': 'Grupo alterado',
  'parametrizacao.grupo.delete': 'Grupo excluido',
  // Carga com aprovacao (P-CARGA).
  'parametrizacao.ramo.activate': 'Ramo ativado (saiu do rascunho)',
  'parametrizacao.grupo.activate': 'Grupo ativado (saiu do rascunho)',
  'parametrizacao.modalidade.activate': 'Modalidade ativada (saiu do rascunho)',
  // Textos, clausulas, coberturas, complementares e documentos (P-PAR-2).
  'parametrizacao.modalidade_objeto.create': 'Rascunho de objeto aberto',
  'parametrizacao.modalidade_objeto.update': 'Rascunho de objeto editado',
  'parametrizacao.modalidade_objeto.approve': 'Versao de objeto ativada',
  'parametrizacao.modalidade_objeto.revoke': 'Versao de objeto revogada',
  'parametrizacao.modalidade_condicao.create': 'Rascunho de condicao aberto',
  'parametrizacao.modalidade_condicao.update': 'Rascunho de condicao editado',
  'parametrizacao.modalidade_condicao.approve': 'Versao de condicao ativada',
  'parametrizacao.modalidade_condicao.revoke': 'Versao de condicao revogada',
  'parametrizacao.clausula_particular.create': 'Condicao particular cadastrada',
  'parametrizacao.clausula_particular.update': 'Condicao particular alterada',
  'parametrizacao.cobertura_adicional.create': 'Cobertura adicional cadastrada',
  'parametrizacao.cobertura_adicional.update': 'Cobertura adicional alterada',
  'parametrizacao.modalidade_complementar.create': 'Modalidade complementar incluida',
  'parametrizacao.modalidade_complementar.update': 'Modalidade complementar alterada',
  'parametrizacao.documento_exigido.create': 'Documento exigido incluido',
  'parametrizacao.documento_exigido.update': 'Documento exigido alterado',
  'parametrizacao.documento_exigido.delete': 'Documento exigido excluido',
};

export function rotuloDaAcao(acao: string): string {
  return acoes[acao] ?? acao;
}

/** Nome dos campos gravados na trilha, com os rotulos da tela. */
const campos: Record<string, string> = {
  code: 'Codigo',
  name: 'Nome',
  description: 'Descricao',
  status: 'Situacao',
  base_rate_bps: 'Taxa base',
  credit_group: 'Grupo de credito',
  credit_reference: 'Referencia de credito',
  modality_group_id: 'Grupo',
  insurer_reference_code: 'Codigo de referencia da seguradora',
  susep_process_number: 'Numero do processo SUSEP',
  product_id: 'Ramo',
  version: 'Versao',
  setting_id: 'Identificador da versao',
  based_on_version: 'Copiada da versao',
  notes: 'Observacao',
  justification: 'Justificativa',
  draft_authors: 'Autores do rascunho',
  activated_by: 'Ativado por (vinculo)',
  activated_label: 'Ativado por',
  revocation_reason: 'Motivo da revogacao',
  max_insured_amount_cents: 'Valor maximo segurado',
  max_auto_approved_amount_cents: 'Valor maximo aprovado automaticamente',
  min_premium_cents: 'Premio minimo',
  min_term_days: 'Vigencia minima (dias)',
  max_term_days: 'Vigencia maxima (dias)',
  max_future_days: 'Maximo de dias futuros',
  retroactivity_handling: 'Tratativa de inicio de vigencia',
  max_retroactive_days: 'Maximo de dias retroativos automaticos',
  auto_issuance_enabled: 'Modalidade habilitada para emissao automatica',
  is_complementary: 'Atuando como modalidade complementar',
  allows_complementary: 'Permite a inclusao de modalidade complementar',
  restricted_for_restricted_activity: 'Restrita para tomadores com ramos restritos',
  equalize_coverage_terms: 'Vigencias entre modalidades e cobertura iguais',
  allows_individual_insured: 'Permite emissao com segurado Pessoa Fisica',
  allows_beneficiary: 'Permite inclusao de beneficiario',
  is_monitored: 'Modalidade monitorada',
  allows_step_in: 'Retomada do objeto (Step-in)',
  is_large_risk: 'Modalidade de grande risco',
  auto_cancel_expired: 'Cancelamento automatico de apolices vencidas',
  modality_rate_enabled: 'Uso de taxa por modalidade',
  ignore_branch_for_individual_insured: 'Ignorar ramo para segurado Pessoa Fisica',
  ignore_branch_for_private_insured: 'Ignorar ramo para segurado privado',
  auto_cancel_days: 'Dias para o cancelamento automatico',
  manual_cancel_days: 'Dias para o cancelamento manual',
  modality_rate_bps: 'Taxa da modalidade',
  accumulation_enabled: 'Acumulo habilitado',
  retroactive_pipe_enabled: 'Esteira para data retroativa',
  is_auctioneer: 'Modalidade de leiloeiro',
  default_economic_group_limit_cents: 'Limite padrao de grupo economico',
  external_product_code: 'Codigo do produto na seguradora',
  // Textos, clausulas, coberturas, complementares e documentos (P-PAR-2).
  term_id: 'Identificador da versao do texto',
  kind: 'Tipo da condicao',
  purpose: 'Finalidade do objeto',
  additional_coverage_id: 'Cobertura adicional',
  insurer_reference: 'Referencia da Seguradora',
  body: 'Texto',
  body_characters: 'Caracteres do texto',
  body_sha256: 'Soma de conferencia do texto',
  enabled_on_quote: 'Habilitar na cotacao',
  position: 'Ordem do bloco',
  tags: 'TAGs de preenchimento automatico',
  clause_id: 'Identificador da condicao',
  broker_can_select: 'Corretor pode inserir?',
  automatic_on_underwriting: 'Automatica Subscricao?',
  aggregator_clause: 'Clausula agregadora',
  free_text: 'Clausula em branco',
  policyholder_id: 'Tomador da clausula',
  coverage_id: 'Identificador da cobertura',
  is_additional: 'Cobertura adicional',
  calculation_type: 'Tipo de Calculo da Cobertura',
  aggravation_rate_bps: 'Taxa de agravamento',
  distribution_rate_bps: 'Taxa de distribuicao',
  calculation_params: 'Parametros do calculo',
  insured_amount_basis: 'Forma da importancia segurada',
  insured_amount_cents: 'Importancia segurada fixa',
  insured_amount_bps: 'Percentual da basica',
  allow_manual_edit: 'Permite edicao manual na cotacao',
  complementary_link_id: 'Identificador do vinculo',
  complementary_modality_id: 'Modalidade complementar',
  required_document_id: 'Identificador do documento',
  scope: 'Lista do documento',
  is_required: 'Obrigatorio',
  pipe_ids: 'Esteiras',
};

export function rotuloDoCampo(campo: string): string {
  return campos[campo] ?? campo;
}
